import { AttachmentList } from "components/Attachments/AttachmentList";
import { DropZone } from "components/CustomUpload/ImageDropZone";
import { useCallback } from "react";
import { FileObject } from "react-mui-dropzone";
import { IAttachment } from "components/Attachments/IAttachment";
import { blobToDataUrl } from "core/blob_operations";
import { v4 as uuidV4 } from "uuid";
import { Stack } from "@mui/material";
import ProgressIndicatorContainer from "components/ProgressIndicatorContainer";

export interface IFileToUpload {
  key: string;
  contents: File;
  attachment: IAttachment;
}
export interface NewAttachmentListProps {
  attachmentsToUpload: IFileToUpload[];
  setAttachmentsToUpload: React.Dispatch<React.SetStateAction<IFileToUpload[]>>;
  setFilesDropping: React.Dispatch<React.SetStateAction<boolean>>;
  filesDropping: boolean;
}

export const NewAttachmentsList = ({
  attachmentsToUpload,
  filesDropping,
  setAttachmentsToUpload,
  setFilesDropping
}: NewAttachmentListProps): JSX.Element => {
  const handleFileUploaded = useCallback(
    async (uploadedFiles: FileObject[]) => {
      if (uploadedFiles.length === 0) {
        setFilesDropping(false);
        return;
      }

      const newAttachments: IFileToUpload[] = uploadedFiles.map(fileObject => {
        const key = uuidV4();
        const filename = fileObject.file.name;
        const isImage = fileObject.file.type.startsWith("image");
        const attachment: IAttachment = {
          filename,
          type: fileObject.file.type,
          onDelete: () =>
            setAttachmentsToUpload(l => l.filter(item => item.key !== key)),
          retrieveImagePreview: isImage
            ? async () => await blobToDataUrl(fileObject.file)
            : undefined
        };

        const fileToUpload: IFileToUpload = {
          key,
          contents: fileObject.file,
          attachment
        };

        return fileToUpload;
      });

      setAttachmentsToUpload(l => [...l, ...newAttachments]);
      setFilesDropping(false);
    },
    [setAttachmentsToUpload, setFilesDropping]
  );

  const handleFileDropped = (): void => {
    setFilesDropping(true);
  };

  return (
    <Stack direction="column" sx={{ width: "100%" }} spacing={1}>
      <ProgressIndicatorContainer loading={filesDropping}>
        <div role="button" aria-label="Drop Zone Container">
          <DropZone
            setFileUploaded={handleFileUploaded}
            setFileDropped={handleFileDropped}
          />
        </div>
        <AttachmentList
          attachments={attachmentsToUpload.map(item => item.attachment)}
        />
      </ProgressIndicatorContainer>
    </Stack>
  );
};
NewAttachmentsList.displayName = "NewAttachmentsList";
